/**
 * Admin: ange mål-FW och grundconfig per enhetsmodell (FOTA-filnamn).
 * P&P körs inte förrän OBD_PROVISIONING_ENABLED=true — konfigurera här först.
 */

import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';

interface BaselineRow {
  id: number;
  device_model: string;
  profile: string;
  desired_firmware_file: string | null;
  desired_config_file: string | null;
  is_default: boolean;
  note: string | null;
}

interface RuntimeConfig {
  enabled: boolean;
  allowlist: string[];
  allowedModels: string[];
}

const ObdBaselineAdminPanel: React.FC = () => {
  const { t } = useTranslation();
  const [baselines, setBaselines] = useState<BaselineRow[]>([]);
  const [runtime, setRuntime] = useState<RuntimeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, { fw: string; cfg: string; note: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{
        success: boolean;
        data: { baselines: BaselineRow[]; runtime: RuntimeConfig };
      }>('/api/device-management/obd-baselines');
      const list = res.data?.data?.baselines ?? [];
      setBaselines(list);
      setRuntime(res.data?.data?.runtime ?? null);
      const next: Record<number, { fw: string; cfg: string; note: string }> = {};
      for (const b of list) {
        next[b.id] = {
          fw: b.desired_firmware_file ?? '',
          cfg: b.desired_config_file ?? '',
          note: b.note ?? '',
        };
      }
      setDrafts(next);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('deviceManagement.obdBaselineLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (row: BaselineRow) => {
    const d = drafts[row.id];
    if (!d) return;
    if (!d.fw.trim() && !d.cfg.trim()) {
      toast.error(t('deviceManagement.obdBaselineNeedFile'));
      return;
    }
    setSavingId(row.id);
    try {
      await api.put(`/api/device-management/obd-baselines/${row.id}`, {
        device_model: row.device_model,
        profile: row.profile,
        desired_firmware_file: d.fw.trim() || null,
        desired_config_file: d.cfg.trim() || null,
        is_default: row.is_default,
        note: d.note.trim() || null,
      });
      toast.success(t('deviceManagement.obdBaselineSaved'));
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || t('deviceManagement.obdBaselineSaveError'));
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="text-sm text-textSecondary">{t('common.loading')}</div>;
  }

  return (
    <div className="mb-6 border border-border rounded-lg p-4 bg-bgCard">
      <h3 className="text-lg font-semibold text-textDefault mb-1">
        {t('deviceManagement.obdBaselineTitle')}
      </h3>
      <p className="text-sm text-textSecondary mb-3">{t('deviceManagement.obdBaselineHelp')}</p>

      {runtime && (
        <div
          className={`mb-3 text-sm rounded px-3 py-2 ${
            runtime.enabled
              ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200'
              : 'bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100'
          }`}
        >
          {runtime.enabled
            ? t('deviceManagement.obdBaselineRuntimeOn')
            : t('deviceManagement.obdBaselineRuntimeOff')}
          {' · '}
          {t('deviceManagement.obdBaselineModels')}: {runtime.allowedModels.join(', ') || 'FMM003'}
        </div>
      )}

      <div className="space-y-4">
        {baselines.map((b) => {
          const d = drafts[b.id] ?? { fw: '', cfg: '', note: '' };
          return (
            <div key={b.id} className="border border-border rounded-lg p-3 space-y-2">
              <div className="font-medium text-textDefault">
                {b.device_model}
                {b.profile ? ` / ${b.profile}` : ` / ${t('deviceManagement.obdBaselineAllProfiles')}`}
              </div>
              <label className="block text-xs text-textSecondary">
                {t('deviceManagement.obdBaselineFw')}
                <input
                  className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-bgElevated font-mono"
                  value={d.fw}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [b.id]: { ...d, fw: e.target.value } }))
                  }
                  placeholder="t.ex. FMM003_04.02.00.Rev.602_…xim"
                />
              </label>
              <label className="block text-xs text-textSecondary">
                {t('deviceManagement.obdBaselineCfg')}
                <input
                  className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-bgElevated font-mono"
                  value={d.cfg}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [b.id]: { ...d, cfg: e.target.value } }))
                  }
                  placeholder="t.ex. FMM003_DQFleet_base.cfg"
                />
              </label>
              <label className="block text-xs text-textSecondary">
                {t('deviceManagement.obdBaselineNote')}
                <input
                  className="mt-1 w-full border border-border rounded px-2 py-1.5 text-sm bg-bgElevated"
                  value={d.note}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [b.id]: { ...d, note: e.target.value } }))
                  }
                />
              </label>
              <button
                type="button"
                disabled={savingId === b.id}
                onClick={() => void save(b)}
                className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          );
        })}
        {baselines.length === 0 && (
          <p className="text-sm text-textSecondary">{t('deviceManagement.obdBaselineEmpty')}</p>
        )}
      </div>
    </div>
  );
};

export default React.memo(ObdBaselineAdminPanel);
